import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Plus, Pencil, Trash2, Info, AlertTriangle, ThumbsUp, ThumbsDown, X, FileText, MapPin } from "@/components/icons";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { plural } from "@/lib/plural";
import { ErrorState } from "@/components/ListState";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// A single solution step is "very long" when it reads like more than one action.
const LONG_STEP_CHARS = 120;

const emptyForm = {
  id: null,
  title: "",
  category_id: "",
  symptoms: [""],
  solution_steps: [""],
  visible_on_qr: true,
  is_draft: false,
};

// ---------------- shared small helpers ----------------
const ListEditor = ({ label, hint, items, onChange, testidPrefix, warnLong }) => {
  const set = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.length > 1 ? items.filter((_, idx) => idx !== i) : [""]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
      </div>
      {hint && <p className="text-data text-muted-foreground">{hint}</p>}
      <div className="space-y-2">
        {items.map((it, i) => {
          const tooLong = warnLong && (it || "").trim().length > LONG_STEP_CHARS;
          return (
            <div key={i} className="space-y-1">
              <div className="flex items-start gap-2">
                {testidPrefix === "step" && (
                  <span className="mt-2 w-5 shrink-0 text-right text-body font-medium tabular-nums text-muted-foreground">{i + 1}.</span>
                )}
                <Textarea
                  rows={testidPrefix === "step" ? 2 : 1}
                  data-testid={`${testidPrefix}-input-${i}`}
                  value={it}
                  onChange={(e) => set(i, e.target.value)}
                  placeholder={testidPrefix === "step" ? "Eén handeling per stap" : "Bijv. geen druk"}
                  className="min-h-0"
                />
                <Button type="button" variant="ghost" size="icon" className="mt-0.5 shrink-0" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => remove(i)}>
                  <X size={18} />
                </Button>
              </div>
              {tooLong && (
                <p className="ml-7 flex items-center gap-1 text-data text-letop-tekst" data-testid={`${testidPrefix}-longwarn-${i}`}>
                  <AlertTriangle size={14} /> Deze stap is vrij lang — kun je 'm opsplitsen in kortere stappen? (niet verplicht)
                </p>
              )}
            </div>
          );
        })}
      </div>
      <Button type="button" variant="outline" size="sm" data-testid={`${testidPrefix}-add`} onClick={add}>
        <Plus size={14} className="mr-1" /> Regel toevoegen
      </Button>
    </div>
  );
};

// ---------------- Articles tab ----------------
const ArticlesTab = ({ categories, catName, openEditorRef, onCounts }) => {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const writable = canWrite();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const guard = useUnsavedGuard(JSON.stringify(form) !== snapshot, () => setOpen(false));

  useEffect(() => {
    if (onCounts) onCounts({ total: articles.length, onQr: articles.filter((a) => a.visible_on_qr).length });
  }, [articles, onCounts]);

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const q = filter === "__all__" ? "" : `?category_id=${filter}`;
      const { data } = await api.get(`/kb-articles${q}`);
      setArticles(data);
    } catch (e) {
      setLoadError(getErrorMessage(e, "Kan artikelen niet laden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const startCreate = () => {
    const f = { ...emptyForm, category_id: categories[0]?.category_id || "" };
    setForm(f);
    setSnapshot(JSON.stringify(f));
    setError("");
    setOpen(true);
  };
  const startEdit = (a) => {
    const f = {
      id: a.id,
      title: a.title || "",
      category_id: a.category_id || "",
      symptoms: a.symptoms?.length ? a.symptoms : [""],
      solution_steps: a.solution_steps?.length ? a.solution_steps : [""],
      visible_on_qr: !!a.visible_on_qr,
      is_draft: !!a.is_draft,
    };
    setForm(f);
    setSnapshot(JSON.stringify(f));
    setError("");
    setOpen(true);
  };
  // Allow the Analyse tab to open a freshly created draft directly in the editor.
  openEditorRef.current = startEdit;

  // ?edit=<id> lets the read view hand an article straight to this editor.
  const editId = searchParams.get("edit");
  useEffect(() => {
    if (!editId || loading) return;
    const found = articles.find((a) => a.id === editId);
    if (found) startEdit(found);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, loading, articles]);

  const clean = (arr) => arr.map((s) => (s || "").trim()).filter(Boolean);

  const submit = async () => {
    setError("");
    if (!form.title.trim()) { setError("Geef het artikel een titel."); return; }
    if (!form.category_id) { setError("Kies een categorie."); return; }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      category_id: form.category_id,
      symptoms: clean(form.symptoms),
      solution_steps: clean(form.solution_steps),
      visible_on_qr: form.visible_on_qr,
      is_draft: form.is_draft,
    };
    try {
      if (form.id) await api.put(`/kb-articles/${form.id}`, body);
      else await api.post("/kb-articles", body);
      toast.success(form.id ? "Artikel bijgewerkt" : "Artikel aangemaakt");
      setOpen(false);
      await load();
    } catch (e) {
      setError(getErrorMessage(e, "Opslaan mislukt."));
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/kb-articles/${a.id}`);
      toast.success("Artikel verwijderd");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="w-full max-w-xs space-y-1.5">
            <Label>Filter op categorie</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="kb-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle categorieën</SelectItem>
                {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {writable && (
            <Button data-testid="kb-new-button" onClick={startCreate}>
              <Plus size={18} className="mr-2" /> Nieuw artikel
            </Button>
          )}
        </div>

        <div>
          {loadError ? (
            <ErrorState testid="kb-load-error" message={loadError} onRetry={load} className="border-0" />
          ) : loading ? (
            <p className="py-6 text-center text-body text-muted-foreground">Laden...</p>
          ) : articles.length === 0 ? (
            // Never column heads above zero rows: say what this is for, and offer the action.
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="kb-empty-state">
              <BookOpen size={28} className="text-muted-foreground" />
              <p className="max-w-md text-body text-foreground">
                Hier zet je op wat de wasstraatmedewerker zélf kan proberen voordat hij de TD belt.
                Wat hier staat, verschijnt na het scannen van de QR-sticker op de machine.
              </p>
              {writable ? (
                <Button data-testid="kb-empty-new-button" onClick={startCreate}>
                  <Plus size={18} className="mr-2" /> Eerste artikel schrijven
                </Button>
              ) : (
                <p className="text-data text-muted-foreground">Je hebt geen rechten om artikelen aan te maken.</p>
              )}
            </div>
          ) : (
          <>
          <Table data-testid="kb-articles-table" wrapperClassName="hidden sm:block">
            <TableHeader>
              <TableRow>
                <TableHead>Titel</TableHead>
                <TableHead>Categorie</TableHead>
                <TableHead>Op QR</TableHead>
                <TableHead>Feedback</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.map((a) => (
                <TableRow
                  key={a.id}
                  data-testid={`kb-row-${a.id}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/kennisbank/${a.id}`)}
                >
                  <TableCell className="text-body font-medium">
                    {a.title}
                    {a.is_draft && <Badge variant="secondary" className="ml-2" data-testid={`kb-draft-badge-${a.id}`}>Concept</Badge>}
                  </TableCell>
                  <TableCell className="text-body">{catName(a.category_id)}</TableCell>
                  <TableCell>
                    {a.visible_on_qr
                      ? <Badge className="border-goed-rand bg-goed-vlak text-goed-tekst">Zichtbaar</Badge>
                      : <Badge variant="secondary">Verborgen</Badge>}
                  </TableCell>
                  <TableCell className="text-body text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><ThumbsUp size={14} /> {a.helpful_count || 0}</span>
                    <span className="ml-3 inline-flex items-center gap-1"><ThumbsDown size={14} /> {a.not_helpful_count || 0}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    {writable ? (
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`kb-edit-${a.id}`} onClick={() => startEdit(a)}><Pencil size={18} /></Button>
                        <DeleteConfirm
                          trigger={<Button variant="ghost" size="icon" data-testid={`kb-delete-${a.id}`}><Trash2 size={18} /></Button>}
                          title={`Artikel "${a.title}" verwijderen?`}
                          description="Dit kan niet ongedaan gemaakt worden."
                          onConfirm={() => remove(a)}
                          testid={`kb-delete-confirm-${a.id}`}
                        />
                      </div>
                    ) : <span className="text-data text-muted-foreground">Alleen lezen</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Phone: one tappable card per artikel. */}
          <div className="divide-y divide-border sm:hidden" data-testid="kb-cardlist">
            {articles.map((a) => (
              <div key={a.id} data-testid={`kb-card-${a.id}`} className="flex items-start justify-between gap-2 py-3">
                <button
                  type="button"
                  className="min-w-0 flex-1 space-y-1 text-left"
                  onClick={() => navigate(`/kennisbank/${a.id}`)}
                >
                  <span className="block text-body font-medium text-foreground">{a.title}</span>
                  <span className="block text-data text-muted-foreground">{catName(a.category_id)}</span>
                  <span className="flex flex-wrap items-center gap-2 pt-0.5">
                    {a.is_draft && <Badge variant="secondary" data-testid={`kb-card-draft-badge-${a.id}`}>Concept</Badge>}
                    {a.visible_on_qr
                      ? <Badge className="border-goed-rand bg-goed-vlak text-goed-tekst">Zichtbaar</Badge>
                      : <Badge variant="secondary">Verborgen</Badge>}
                    <span className="inline-flex items-center gap-1 text-data text-muted-foreground"><ThumbsUp size={14} /> {a.helpful_count || 0}</span>
                    <span className="inline-flex items-center gap-1 text-data text-muted-foreground"><ThumbsDown size={14} /> {a.not_helpful_count || 0}</span>
                  </span>
                </button>
                {writable && (
                  <div className="flex shrink-0 items-center">
                    <Button variant="ghost" size="icon" data-testid={`kb-card-edit-${a.id}`} onClick={() => startEdit(a)}><Pencil size={18} /></Button>
                    <DeleteConfirm
                      trigger={<Button variant="ghost" size="icon" data-testid={`kb-card-delete-${a.id}`}><Trash2 size={18} /></Button>}
                      title={`Artikel "${a.title}" verwijderen?`}
                      description="Dit kan niet ongedaan gemaakt worden."
                      onConfirm={() => remove(a)}
                      testid={`kb-card-delete-confirm-${a.id}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent className="max-w-2xl">
          <DiscardDialog guard={guard} />
          <DialogHeader>
            <DialogTitle>{form.id ? "Artikel bewerken" : "Nieuw artikel"}</DialogTitle>
            <DialogDescription className="flex items-center gap-1.5 text-wacht-tekst">
              <Info size={14} /> Schrijf zoals je het aan een nieuwe collega zou uitleggen: korte, duidelijke stappen — één handeling per stap.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Titel</Label>
              <Input data-testid="kb-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Bijv. Geen druk op de hogedrukpomp" />
            </div>
            <div className="space-y-1.5">
              <Label>Categorie</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                <SelectTrigger data-testid="kb-category-select"><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <ListEditor
              label="Klachten / symptomen"
              hint="Waar merkt iemand het aan? Kort, in gewone taal."
              items={form.symptoms}
              onChange={(v) => setForm((f) => ({ ...f, symptoms: v }))}
              testidPrefix="symptom"
            />

            <ListEditor
              label="Oplossing (stap voor stap)"
              hint="Eén handeling per stap. Houd het kort en concreet."
              items={form.solution_steps}
              onChange={(v) => setForm((f) => ({ ...f, solution_steps: v }))}
              testidPrefix="step"
              warnLong
            />

            <div className="rounded-md border border-dashed border-border p-3 text-data text-muted-foreground">
              Foto's en video bij een artikel kunnen straks worden toegevoegd zodra media-opslag is geconfigureerd.
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label className="text-body">Tonen op QR-pagina</Label>
                <p className="text-data text-muted-foreground">Zichtbaar voor iedereen die de QR-sticker scant.</p>
              </div>
              <Switch data-testid="kb-visible-switch" checked={form.visible_on_qr} onCheckedChange={(v) => setForm((f) => ({ ...f, visible_on_qr: v }))} />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label className="text-body">Concept</Label>
                <p className="text-data text-muted-foreground">Nog niet af — laat op concept staan tot het klaar is.</p>
              </div>
              <Switch data-testid="kb-draft-switch" checked={form.is_draft} onCheckedChange={(v) => setForm((f) => ({ ...f, is_draft: v }))} />
            </div>

            {error && <p className="text-body text-destructive" data-testid="kb-editor-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => guard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="kb-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------- Analyse tab ----------------
const AnalyseTab = ({ catName, onCreatedDraft, goToArticles }) => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/faults/analysis"); setData(data); }
    catch (e) { toast.error(getErrorMessage(e, "Kan analyse niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const makeDraft = async (category_id) => {
    setCreating(category_id);
    try {
      const { data } = await api.post(`/kb-articles/draft-from-category?category_id=${encodeURIComponent(category_id)}`);
      toast.success("Concept-artikel aangemaakt met symptomen vooringevuld");
      goToArticles();
      // small delay so the Articles tab mounts before opening the editor
      setTimeout(() => onCreatedDraft(data), 60);
    } catch (e) {
      toast.error(getErrorMessage(e, "Aanmaken mislukt."));
    } finally { setCreating(null); }
  };

  if (loading) return <p className="text-body text-muted-foreground">Laden...</p>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <Card className="p-5" data-testid="analysis-without-article">
        <h2 className="mb-1 flex items-center gap-2 text-body font-semibold text-foreground">
          <AlertTriangle size={18} className="text-letop-tekst" /> Meest gemeld zonder artikel
        </h2>
        <p className="mb-3 text-body text-muted-foreground">Categorieën met storingen maar (nog) geen kennisartikel. Maak er in één klik een concept van.</p>
        {data.most_reported_without_article.length === 0 ? (
          <p className="text-body text-muted-foreground">Elke categorie met meldingen heeft al minstens één artikel. </p>
        ) : (
          <ul className="space-y-2">
            {data.most_reported_without_article.map((g) => (
              <li key={g.category_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3" data-testid={`without-article-${g.category_id}`}>
                <div>
                  <div className="text-body font-medium text-foreground">{g.category_name}</div>
                  <div className="text-data text-muted-foreground">{plural(g.count, "melding", "meldingen")}{g.descriptions?.length ? ` — bijv. "${g.descriptions[0]}"` : ""}</div>
                </div>
                {writable && (
                  <Button size="sm" variant="outline" data-testid={`make-draft-${g.category_id}`} disabled={creating === g.category_id} onClick={() => makeDraft(g.category_id)}>
                    <FileText size={14} className="mr-1" /> {creating === g.category_id ? "Bezig..." : "Concept-artikel maken"}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5" data-testid="analysis-by-category">
          <h2 className="mb-3 text-body font-semibold text-foreground">Storingen per categorie</h2>
          {data.by_category.length === 0 ? (
            <p className="text-body text-muted-foreground">Zodra er storingen gemeld zijn, zie je hier per categorie hoeveel dat waren en of er al een artikel bij hoort.</p>
          ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Categorie</TableHead><TableHead className="text-right">Meldingen</TableHead><TableHead className="text-right">Artikelen</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.by_category.map((g) => (
                <TableRow key={g.category_id}>
                  <TableCell className="text-body">{g.category_name}</TableCell>
                  <TableCell className="text-right text-body tabular-nums">{g.count}</TableCell>
                  <TableCell className="text-right text-body tabular-nums">{g.article_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </Card>

        <Card className="p-5" data-testid="analysis-by-location">
          <h2 className="mb-3 flex items-center gap-2 text-body font-semibold text-foreground"><MapPin size={18} /> Storingen per locatie</h2>
          {data.by_location.length === 0 ? (
            <p className="text-body text-muted-foreground">Zodra er storingen gemeld zijn, zie je hier welke vestiging het vaakst belt.</p>
          ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Locatie</TableHead><TableHead className="text-right">Meldingen</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.by_location.map((g) => (
                <TableRow key={g.location_id}>
                  <TableCell className="text-body">{g.location_name}</TableCell>
                  <TableCell className="text-right text-body tabular-nums">{g.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </Card>
      </div>
    </div>
  );
};

export default function KnowledgeBase() {
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("artikelen");
  const { canView } = useAuth();
  const showAnalysis = canView("storingen");
  const openEditorRef = React.useRef(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/asset-categories"); setCategories(data); }
      catch (e) { toast.error(getErrorMessage(e, "Kan categorieën niet laden.")); }
    })();
  }, []);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.category_id, c.name])), [categories]);
  const catName = (id) => catMap[id] || id;

  // The console says how much is in here; the tab that owns the list reports it.
  const [counts, setCounts] = useState(null);
  const countFacts = counts ? [`${counts.total} artikelen`, `${counts.onQr} op QR`] : [];


  return (
    <Page console={{ variant: "view", title: "Kennisbank",
      facts: countFacts }}>
      <div className="space-y-4">

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="artikelen" data-testid="kb-tab-articles">Artikelen</TabsTrigger>
          {showAnalysis && <TabsTrigger value="analyse" data-testid="kb-tab-analysis">Analyse</TabsTrigger>}
        </TabsList>
        <TabsContent value="artikelen" className="mt-4">
          <ArticlesTab categories={categories} catName={catName} openEditorRef={openEditorRef} onCounts={setCounts} />
        </TabsContent>
        {showAnalysis && (
          <TabsContent value="analyse" className="mt-4">
            <AnalyseTab
              catName={catName}
              goToArticles={() => setTab("artikelen")}
              onCreatedDraft={(doc) => openEditorRef.current && openEditorRef.current(doc)}
            />
          </TabsContent>
        )}
      </Tabs>
      </div>
    </Page>
  );
}
